// src/components/StationPhoto/StationPhoto.jsx
import React, { useState, useEffect } from 'react';
import { FaChargingStation } from 'react-icons/fa';
import './StationPhoto.less';

/**
 * StationPhoto — the single component that renders a station image
 * everywhere in the customer app. All station-photo rendering
 * (charger profile hero, map preview, list/nearby cards) flows
 * through here so loading, error, placeholder, and sizing behave
 * identically across surfaces.
 *
 * Sizing variants
 * ---------------
 * The four operator-visible sizes are named rather than pixel-sized
 * so a future design tweak is a one-line change:
 *
 *   'thumbnail'  — small square (list cards, dense rows). 72px on
 *                  mobile, 88px on ≥tablet; 1:1 aspect ratio; smaller
 *                  radius; smaller placeholder icon.
 *   'card'       — full-width 16:9 image with card-radius. Used by
 *                  the map preview / bottom sheet.
 *   'hero'       — the charger-profile top hero. 3:2 aspect ratio
 *                  scales responsively (fluid width, height derived
 *                  from ratio) so the image height on desktop
 *                  matches the eye-line of the content beneath.
 *   'custom'     — legacy behaviour: caller supplies `height`.
 *                  Preserved so existing sites that pass `height`
 *                  don't need updating.
 *
 * Aspect-ratio placeholder
 * ------------------------
 * Every non-'custom' variant reserves its final space via
 * `aspect-ratio` on the container BEFORE the image loads. That
 * eliminates layout shift when the image lands — the placeholder,
 * skeleton, and image all live inside the same reserved box.
 *
 * Loading + fade-in
 * -----------------
 * When a photoUrl is supplied we start in a 'loading' state that
 * renders the shared `.gc-skeleton` shimmer inside the reserved
 * box. `onLoad` transitions to 'loaded' and the image fades in
 * over 220ms. `onError` transitions to 'error' and the placeholder
 * icon shows. A URL change resets all three states.
 *
 * Lazy loading
 * ------------
 * Native `loading="lazy"` on the img element defers offscreen
 * decoding. The `hero` variant defaults to eager loading — it's
 * always above the fold on the charger profile — but callers can
 * override via the `loading` prop.
 *
 * Accessibility
 * -------------
 * A meaningful `alt` prop is required for hero and card variants;
 * thumbnails default to empty alt (decorative — the adjacent text
 * carries the semantics). The placeholder state has aria-hidden so
 * screen readers don't announce "charging station icon" for every
 * station in a list.
 *
 * @param {Object} props
 * @param {string|null} props.photoUrl — Full URL to the station photo.
 * @param {string} [props.alt] — Alt text; required for hero/card.
 * @param {'thumbnail'|'card'|'hero'|'custom'} [props.variant='custom']
 * @param {number} [props.height] — Only used when variant='custom'.
 *                                   Backward-compat.
 * @param {'lazy'|'eager'} [props.loading] — Native loading attr;
 *                                   defaults to 'eager' for hero,
 *                                   'lazy' otherwise.
 * @param {string} [props.className] — Extra classes on the outer div.
 */
const StationPhoto = ({
  photoUrl,
  alt = 'EV charging station',
  variant = 'custom',
  height = 220,
  loading,
  className = '',
}) => {
  const [status, setStatus] = useState(() => (photoUrl ? 'loading' : 'placeholder'));

  // Reset the state machine on every photoUrl change. This is the
  // regression fix for the sticky-error latch: a new valid URL
  // must ALWAYS get a fresh loading pass, and a URL clearing to
  // null must return to the placeholder.
  useEffect(() => {
    setStatus(photoUrl ? 'loading' : 'placeholder');
  }, [photoUrl]);

  const handleImageLoad = () => setStatus('loaded');
  const handleImageError = () => {
    // Console noise is not helpful in the customer app — the UI
    // fallback (placeholder) is the operator-visible signal.
    setStatus('error');
  };

  const isPlaceholder = status === 'placeholder' || status === 'error';
  const isLoading = status === 'loading';
  const isLoaded = status === 'loaded';

  // The `custom` variant preserves the legacy `height={px}` API so
  // no existing site needs to be touched to keep working. Every
  // named variant uses aspect-ratio + a container class instead.
  const containerStyle = variant === 'custom' ? { height: `${height}px` } : undefined;
  const effectiveLoading = loading || (variant === 'hero' ? 'eager' : 'lazy');

  return (
    <div
      className={[
        'station-photo',
        `station-photo--${variant}`,
        isLoaded ? 'is-loaded' : '',
        className,
      ].filter(Boolean).join(' ')}
      style={containerStyle}
    >
      {isPlaceholder && (
        <div className="station-photo__placeholder" aria-hidden="true">
          <FaChargingStation className="station-photo__icon" />
        </div>
      )}
      {isLoading && (
        <div className="station-photo__skeleton" aria-hidden="true" />
      )}
      {/* Image is always rendered when we have a URL so the browser
          can start the fetch immediately; we only visually reveal it
          once onLoad fires. Skeleton overlays until then. */}
      {photoUrl && !isPlaceholder && (
        <img
          src={photoUrl}
          alt={alt}
          loading={effectiveLoading}
          decoding="async"
          onLoad={handleImageLoad}
          onError={handleImageError}
          className="station-photo__image"
        />
      )}
    </div>
  );
};

export default StationPhoto;
